/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  confirmPasswordReset,
  verifyPasswordResetCode,
} from "firebase/auth";
import { auth } from "../firebase";
import presentPlusLogo from "../assets/presentplus-logo-white.png";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const mode = searchParams.get("mode");
  const oobCode = searchParams.get("oobCode");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmValue, setConfirmValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const canSubmit = useMemo(() => {
    return (
      password.trim().length >= 8 &&
      confirmValue.trim().length >= 8 &&
      password === confirmValue &&
      !!oobCode &&
      mode === "resetPassword"
    );
  }, [password, confirmValue, oobCode, mode]);

  useEffect(() => {
    async function init() {
      setLoading(true);
      setError("");

      if (mode !== "resetPassword" || !oobCode) {
        setError("This reset link is incomplete or invalid.");
        setLoading(false);
        return;
      }

      try {
        const resolvedEmail = await verifyPasswordResetCode(auth, oobCode);
        setEmail(resolvedEmail || "");
      } catch {
        setError("This reset link is invalid or has expired.");
      }

      setLoading(false);
    }

    init();
  }, [mode, oobCode]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      await confirmPasswordReset(auth, oobCode, password);
      setSuccess("Password saved successfully. Redirecting to login...");
      setTimeout(() => navigate("/login"), 1300);
    } catch {
      setError("Unable to reset password. Please request a new link.");
    }

    setSaving(false);
  }

  return (
    <div style={styles.page}>
      <style>{`
        @keyframes ppCardIn {
          from { opacity: 0; transform: translateY(18px) scale(.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        @keyframes ppLogoPulse {
          0%,100% { transform: scale(1); box-shadow: 0 22px 60px rgba(255,255,255,.16); }
          50% { transform: scale(1.035); box-shadow: 0 28px 80px rgba(255,255,255,.24); }
        }

        @keyframes ppOrb {
          0%,100% { transform: translate3d(0,0,0) scale(1); }
          50% { transform: translate3d(18px,-16px,0) scale(1.08); }
        }

        @keyframes ppDot {
          0%, 80%, 100% { transform: scale(.72); opacity: .45; }
          40% { transform: scale(1); opacity: 1; }
        }
      `}</style>

      <div style={styles.orbOne} />
      <div style={styles.orbTwo} />

      <main style={styles.card}>
        <div style={styles.brandShell}>
          <img src={presentPlusLogo} alt="PresentPlus" style={styles.logo} />
        </div>

        <div style={styles.eyebrow}>PresentPlus Secure Access</div>

        <h1 style={styles.title}>
          {success ? "Password updated" : "Set your password"}
        </h1>

        <p style={styles.subtitle}>
          {loading
            ? "Checking your secure link..."
            : email
            ? `For ${email}`
            : "Choose a strong new password for your account."}
        </p>

        {loading ? (
          <div style={styles.loadingBox}>
            <div style={styles.dots}>
              <span style={{ ...styles.dot, animationDelay: "0ms" }} />
              <span style={{ ...styles.dot, animationDelay: "160ms" }} />
              <span style={{ ...styles.dot, animationDelay: "320ms" }} />
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={styles.form}>
            <PasswordField
              value={password}
              onChange={setPassword}
              visible={showPassword}
              setVisible={setShowPassword}
              placeholder="New password"
              disabled={saving || !!success || !!error && !oobCode}
            />

            <PasswordField
              value={confirmValue}
              onChange={setConfirmValue}
              visible={showConfirm}
              setVisible={setShowConfirm}
              placeholder="Confirm new password"
              disabled={saving || !!success || !!error && !oobCode}
            />

            <div style={styles.helper}>
              Use at least 8 characters and make sure both passwords match.
            </div>

            {error ? <div style={styles.error}>{error}</div> : null}
            {success ? <div style={styles.success}>{success}</div> : null}

            <button
              type="submit"
              style={{
                ...styles.submit,
                ...(!canSubmit || saving || success ? styles.submitDisabled : {}),
              }}
              disabled={!canSubmit || saving || !!success}
            >
              {saving ? "Saving..." : "Save password"}
            </button>

            <button
              type="button"
              style={styles.back}
              onClick={() => navigate("/login")}
            >
              Back to login
            </button>
          </form>
        )}
      </main>
    </div>
  );
}

function PasswordField({
  value,
  onChange,
  visible,
  setVisible,
  placeholder,
  disabled,
}) {
  return (
    <div style={styles.inputWrap}>
      <span style={styles.lockIcon}>⌁</span>

      <input
        style={styles.input}
        type={visible ? "text" : "password"}
        placeholder={placeholder}
        autoComplete="new-password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />

      <button
        type="button"
        style={styles.eyeButton}
        onClick={() => setVisible((v) => !v)}
        disabled={disabled}
      >
        {visible ? "Hide" : "Show"}
      </button>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    position: "relative",
    overflow: "hidden",
    display: "grid",
    placeItems: "center",
    padding: 20,
    boxSizing: "border-box",
    background:
      "radial-gradient(circle at 30% 22%, rgba(112,150,255,.42), transparent 34%), linear-gradient(135deg,#083ea8 0%,#0f49bc 42%,#1538a7 100%)",
  },
  orbOne: {
    position: "absolute",
    width: 380,
    height: 380,
    borderRadius: 999,
    background: "rgba(255,255,255,.11)",
    top: -110,
    right: -90,
    animation: "ppOrb 6s ease-in-out infinite",
  },
  orbTwo: {
    position: "absolute",
    width: 300,
    height: 300,
    borderRadius: 999,
    background: "rgba(107,123,255,.22)",
    bottom: -90,
    left: -80,
    animation: "ppOrb 7s ease-in-out infinite reverse",
  },
  card: {
    width: "100%",
    maxWidth: 450,
    position: "relative",
    zIndex: 2,
    borderRadius: 34,
    padding: 30,
    boxSizing: "border-box",
    background: "rgba(255,255,255,.14)",
    border: "1px solid rgba(255,255,255,.22)",
    boxShadow: "0 32px 90px rgba(0,0,0,.22)",
    backdropFilter: "blur(22px)",
    color: "#fff",
    animation: "ppCardIn .55s ease-out both",
  },
  brandShell: {
    width: 90,
    height: 90,
    borderRadius: 28,
    display: "grid",
    placeItems: "center",
    margin: "0 auto 18px",
    background: "rgba(255,255,255,.12)",
    border: "1px solid rgba(255,255,255,.22)",
    animation: "ppLogoPulse 2.5s ease-in-out infinite",
  },
  logo: {
    width: 64,
    maxHeight: 64,
    objectFit: "contain",
  },
  eyebrow: {
    textAlign: "center",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: ".16em",
    fontWeight: 900,
    color: "rgba(255,255,255,.76)",
    marginBottom: 10,
  },
  title: {
    margin: 0,
    textAlign: "center",
    fontSize: 36,
    lineHeight: 1,
    letterSpacing: "-.045em",
    fontWeight: 950,
  },
  subtitle: {
    margin: "14px auto 22px",
    textAlign: "center",
    color: "rgba(255,255,255,.82)",
    lineHeight: 1.6,
    fontSize: 14,
    maxWidth: 340,
  },
  loadingBox: {
    display: "grid",
    placeItems: "center",
    padding: "24px 0 8px",
  },
  dots: {
    display: "flex",
    gap: 8,
  },
  dot: {
    width: 9,
    height: 9,
    borderRadius: 999,
    background: "#ffffff",
    animation: "ppDot 1.2s ease-in-out infinite",
  },
  form: {
    display: "grid",
    gap: 12,
  },
  inputWrap: {
    display: "grid",
    gridTemplateColumns: "38px 1fr auto",
    alignItems: "center",
    borderRadius: 18,
    background: "rgba(255,255,255,.95)",
    border: "1px solid rgba(255,255,255,.35)",
    padding: "0 12px",
    minHeight: 58,
  },
  lockIcon: {
    color: "#4f46e5",
    fontWeight: 900,
    fontSize: 20,
  },
  input: {
    border: "none",
    outline: "none",
    background: "transparent",
    color: "#102044",
    fontSize: 15,
    fontWeight: 700,
    minWidth: 0,
  },
  eyeButton: {
    border: "none",
    background: "transparent",
    color: "#4f46e5",
    fontWeight: 900,
    cursor: "pointer",
    padding: "8px 0 8px 12px",
  },
  helper: {
    fontSize: 12,
    color: "rgba(255,255,255,.78)",
    lineHeight: 1.55,
  },
  error: {
    borderRadius: 16,
    padding: 13,
    background: "rgba(254,226,226,.95)",
    color: "#991b1b",
    fontWeight: 850,
    fontSize: 13,
  },
  success: {
    borderRadius: 16,
    padding: 13,
    background: "rgba(220,252,231,.95)",
    color: "#166534",
    fontWeight: 850,
    fontSize: 13,
  },
  submit: {
    border: "none",
    borderRadius: 18,
    padding: "15px 16px",
    background: "#ffffff",
    color: "#123db4",
    fontWeight: 950,
    fontSize: 15,
    cursor: "pointer",
    boxShadow: "0 18px 44px rgba(0,0,0,.14)",
  },
  submitDisabled: {
    opacity: 0.52,
    cursor: "not-allowed",
  },
  back: {
    border: "1px solid rgba(255,255,255,.18)",
    borderRadius: 18,
    padding: "14px 16px",
    background: "rgba(255,255,255,.08)",
    color: "#ffffff",
    fontWeight: 900,
    cursor: "pointer",
  },
};